import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionType: string;
  categoryFormData: {
    categoryName: string;
    categoryCode: string;
    categoryDescription: string;
  };
  setCategoryFormData: (data: any) => void;
  categoryLogo: File | null;
  setCategoryLogo: (file: File | null) => void;
  categoryLogoPreview: string | null;
  setCategoryLogoPreview: (preview: string | null) => void;
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

export const CategoryFormDialog: React.FC<CategoryFormDialogProps> = ({
  open,
  onOpenChange,
  actionType,
  categoryFormData,
  setCategoryFormData,
  categoryLogo,
  setCategoryLogo,
  categoryLogoPreview,
  setCategoryLogoPreview,
  isSubmitting,
  onSubmit,
  onCancel,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {actionType === "Edit Category"
              ? "Edit Category"
              : actionType === "Add Sub-Category"
              ? "Add Sub-Category"
              : "Add Category"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Category Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Image
            </label>
            <div className="space-y-3">
              {/* Image Preview */}
              {(categoryLogoPreview || categoryLogo) && (
                <div className="flex items-center space-x-3">
                  <img
                    src={
                      categoryLogo
                        ? URL.createObjectURL(categoryLogo)
                        : categoryLogoPreview
                        ? `${
                            import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
                          }${categoryLogoPreview}`
                        : "/placeholder.svg"
                    }
                    alt="Category preview"
                    className="w-16 h-16 object-cover rounded border bg-gray-50"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/placeholder.svg";
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">
                      {categoryLogo ? categoryLogo.name : "Current image"}
                    </p>
                    {categoryLogo && (
                      <button
                        type="button"
                        onClick={() => {
                          setCategoryLogo(null);
                          setCategoryLogoPreview(null);
                        }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Remove new image
                      </button>
                    )}
                  </div>
                </div>
              )}
              {/* File Input */}
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setCategoryLogo(file);
                    if (file) {
                      setCategoryLogoPreview(null); // Clear existing preview when new file is selected
                    }
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>
          </div>

          {/* Category Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Name *
            </label>
            <Input
              value={categoryFormData.categoryName}
              onChange={(e) =>
                setCategoryFormData((prev) => ({
                  ...prev,
                  categoryName: e.target.value,
                }))
              }
              placeholder="Enter category name"
            />
          </div>

          {/* Category Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Code *
            </label>
            <Input
              value={categoryFormData.categoryCode}
              onChange={(e) =>
                setCategoryFormData((prev) => ({
                  ...prev,
                  categoryCode: e.target.value,
                }))
              }
              placeholder="Enter category code"
              disabled={actionType === "Edit Category"}
              className={actionType === "Edit Category" ? "bg-gray-100" : ""}
            />
          </div>

          {/* Category Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Description
            </label>
            <textarea
              value={categoryFormData.categoryDescription}
              onChange={(e) =>
                setCategoryFormData((prev) => ({
                  ...prev,
                  categoryDescription: e.target.value,
                }))
              }
              placeholder="Enter category description"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              isSubmitting ||
              !categoryFormData.categoryName ||
              !categoryFormData.categoryCode
            }
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
